import { useState, useEffect, useCallback } from "react";
import { FaEdit, FaTrash, FaTimes, FaEye, FaArrowLeft, FaArrowRight, FaStar } from "react-icons/fa";
import { utils, writeFile } from "xlsx";
import axios from "axios";

export default function TrendingPosters() {
    const [posters, setPosters] = useState([]);
    const [allPosters, setAllPosters] = useState([]);
    const [categoryId, setCategoryId] = useState("");
    const [categoryName, setCategoryName] = useState("");
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState("");
    const [filterType, setFilterType] = useState("trending"); // trending or all

    // Modal states
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedPoster, setSelectedPoster] = useState(null);
    const [editedData, setEditedData] = useState({
        name: "",
        categoryName: "",
        title: "",
        description: "",
        posterlang: "",
        festivalDate: "",
        email: "",
        mobile: "",
        tags: ""
    });

    const [previewModalImage, setPreviewModalImage] = useState(null);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [viewingPoster, setViewingPoster] = useState(null);

    // Fetch all categories for filter
    const fetchCategories = useCallback(async () => {
        try {
            const response = await axios.get("https://api.editezy.com/api/poster/categories");
            if (response.data.success) {
                setCategories(response.data.categories);
            }
        } catch (error) {
            console.error("Error fetching categories:", error);
        }
    }, []);

    // Fetch all posters from subcategori/all endpoint
    const fetchAllPosters = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get("https://api.editezy.com/api/poster/subcategori/all");

            if (response.data.success) {
                setAllPosters(response.data.posters || []);

                // Filter based on selected filter type and category
                let filtered = response.data.posters || [];

                // Filter by trending status
                if (filterType === "trending") {
                    filtered = filtered.filter(poster => poster.isTrending === true);
                }

                // Filter by category if selected
                if (selectedCategory) {
                    filtered = filtered.filter(poster => poster.categoryId === selectedCategory);
                }

                // Filter by search term
                if (search) {
                    const searchLower = search.toLowerCase();
                    filtered = filtered.filter(poster =>
                        poster.name?.toLowerCase().includes(searchLower) ||
                        poster.title?.toLowerCase().includes(searchLower) ||
                        poster.description?.toLowerCase().includes(searchLower)
                    );
                }

                // Apply pagination
                const startIndex = (currentPage - 1) * 10;
                const endIndex = startIndex + 10;
                const paginatedPosters = filtered.slice(startIndex, endIndex);

                setPosters(paginatedPosters);
                setTotalCount(filtered.length);
                setTotalPages(Math.ceil(filtered.length / 10));
            }
        } catch (error) {
            console.error("Error fetching all posters:", error);
        } finally {
            setLoading(false);
        }
    }, [currentPage, search, selectedCategory, filterType]);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    useEffect(() => {
        fetchAllPosters();
    }, [fetchAllPosters]);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            if (currentPage !== 1) setCurrentPage(1);
            else fetchAllPosters();
        }, 500);
        return () => clearTimeout(timer);
    }, [search]);

    // Handle category filter change
    const handleCategoryChange = (e) => {
        setSelectedCategory(e.target.value);
        setCurrentPage(1);
    };

    // Handle filter type change
    const handleFilterTypeChange = (type) => {
        setFilterType(type);
        setCurrentPage(1);
    };

    // Update trending status
    const handleToggleTrending = async (id, currentStatus) => {
        try {
            const response = await axios.put(
                `https://api.editezy.com/api/poster/update-trending/${id}`,
                { isTrending: !currentStatus }
            );
            if (response.data.success) {
                fetchAllPosters();
                alert(`Poster ${!currentStatus ? "added to" : "removed from"} trending successfully!`);
            }
        } catch (error) {
            console.error("Error updating trending status:", error);
            alert("Error updating trending status.");
        }
    };

    // Delete poster
    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this poster?")) return;
        try {
            await axios.delete(`https://api.editezy.com/api/poster/subcategori/delete/${id}`);
            fetchAllPosters();
            alert("Poster deleted successfully!");
        } catch (error) {
            console.error("Delete error:", error);
            alert("Error deleting poster.");
        }
    };

    // Edit poster
    const handleEdit = (poster) => {
        setSelectedPoster(poster);
        setEditedData({
            name: poster.name || "",
            categoryName: poster.categoryName || "",
            title: poster.title || "",
            description: poster.description || "",
            posterlang: poster.posterlang || "",
            festivalDate: poster.festivalDate ? poster.festivalDate.split('T')[0] : "",
            email: poster.email || "",
            mobile: poster.mobile || "",
            tags: Array.isArray(poster.tags) ? poster.tags.join(", ") : ""
        });
        setModalOpen(true);
    };

    const handleSaveChanges = async () => {
        try {
            const updateData = {
                ...editedData,
                tags: editedData.tags.split(",").map(tag => tag.trim())
            };
            await axios.put(`https://api.editezy.com/api/poster/subcategori/update/${selectedPoster._id}`, updateData);
            fetchAllPosters();
            alert("Poster updated successfully!");
            setModalOpen(false);
            setSelectedPoster(null);
        } catch (error) {
            console.error("Error updating poster:", error);
            alert("Error updating the poster.");
        }
    };

    // View poster details
    const handleView = (poster) => {
        setViewingPoster(poster);
        setViewModalOpen(true);
    };

    // Export data
    const exportData = (type) => {
        const exportPosters = posters.map((poster, i) => ({
            "SI": (currentPage - 1) * 10 + i + 1,
            "ID": poster._id,
            "Poster Name": poster.name || "N/A",
            "Category": poster.categoryName || "N/A",
            "Title": poster.title || "N/A",
            "Description": poster.description || "N/A",
            "Poster Language": poster.posterlang || "N/A",
            "Trending": poster.isTrending ? "Yes" : "No",
            "Festival Date": poster.festivalDate ? new Date(poster.festivalDate).toLocaleDateString() : "N/A",
            "Email": poster.email || "N/A",
            "Mobile": poster.mobile || "N/A",
            "Tags": Array.isArray(poster.tags) ? poster.tags.join(", ") : "N/A",
            "Created At": poster.createdAt ? new Date(poster.createdAt).toLocaleString() : "N/A"
        }));
        const ws = utils.json_to_sheet(exportPosters);
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, "Posters");
        writeFile(wb, `posters_${filterType}.${type}`);
    };

    console.log("new ciomponent rendered");

    return (
        <div className="p-4 border rounded-lg shadow bg-white">
            {/* Header */}
            <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
                <div>
                    <h2 className="text-xl font-semibold text-blue-900">
                        {filterType === "trending" ? "Trending Posters" : "All Posters"}
                    </h2>
                    {totalCount > 0 && (
                        <p className="text-sm text-gray-600 mt-1">
                            Total {filterType === "trending" ? "Trending" : ""} Posters: {totalCount}
                        </p>
                    )}
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => exportData("csv")}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition"
                    >
                        CSV
                    </button>
                    <button
                        onClick={() => exportData("xlsx")}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded transition"
                    >
                        Excel
                    </button>
                </div>
            </div>

            {/* Filter Tabs */}
            <div className="flex gap-2 mb-4 border-b">
                <button
                    onClick={() => handleFilterTypeChange("trending")}
                    className={`px-4 py-2 font-medium transition ${filterType === "trending"
                        ? "text-yellow-600 border-b-2 border-yellow-600"
                        : "text-gray-500 hover:text-gray-700"
                        }`}
                >
                    <FaStar className="inline mr-1" /> Trending Posters
                </button>
                {/* <button
                    onClick={() => handleFilterTypeChange("all")}
                    className={`px-4 py-2 font-medium transition ${
                        filterType === "all"
                            ? "text-blue-600 border-b-2 border-blue-600"
                            : "text-gray-500 hover:text-gray-700"
                    }`}
                >
                    All Posters
                </button> */}
            </div>

            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <input
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none"
                    placeholder="Search by poster name, title, or description..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <select
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none"
                    value={selectedCategory}
                    onChange={handleCategoryChange}
                >
                    <option value="">All Categories</option>
                    {categories.map((category) => (
                        <option key={category._id} value={category._id}>
                            {category.name}
                        </option>
                    ))}
                </select>
            </div>

            {/* Loading State */}
            {loading ? (
                <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <p className="mt-2 text-gray-600">Loading posters...</p>
                </div>
            ) : (
                <>
                    {/* Table */}
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse border border-gray-300">
                            <thead>
                                <tr className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
                                    <th className="p-2 border">#</th>
                                    <th className="p-2 border">Poster Image</th>
                                    <th className="p-2 border">Category</th>
                                    <th className="p-2 border">subCategory</th>
                                    <th className="p-2 border">Trending</th>
                                    <th className="p-2 border">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {posters.length === 0 ? (
                                    <tr>
                                        <td colSpan="10" className="text-center p-8 text-gray-500">
                                            No posters found
                                        </td>
                                    </tr>
                                ) : (
                                    posters.map((poster, index) => (
                                        <tr key={poster._id} className="border-b hover:bg-gray-50 transition">
                                            <td className="p-2 border text-center">
                                                {(currentPage - 1) * 10 + index + 1}
                                            </td>
                                            <td className="p-2 border">
                                                {poster.posterImage ? (
                                                    <img
                                                        src={typeof poster.posterImage === 'string' ? poster.posterImage : poster.posterImage.url}
                                                        alt={poster.title || "poster"}
                                                        className="w-12 h-12 object-cover rounded cursor-pointer hover:opacity-80 transition"
                                                        onClick={() => setPreviewModalImage(
                                                            typeof poster.posterImage === 'string' ? poster.posterImage : poster.posterImage.url
                                                        )}
                                                        onError={(e) => {
                                                            e.target.onerror = null;
                                                            e.target.style.display = "none";
                                                        }} />
                                                ) : (
                                                    <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center text-xs text-gray-500">
                                                        No Image
                                                    </div>
                                                )}
                                            </td>
                                            <td className="p-2 border">
                                                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                                                    {poster.categoryName || "N/A"}
                                                </span>
                                            </td>
                                            <td className="p-2 border">
                                                <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                                                    {poster.name || "N/A"}
                                                </span>
                                            </td>
                                            <td className="p-2 border text-center">
                                                {poster.isTrending ? (
                                                    <button
                                                        onClick={() => handleToggleTrending(poster._id, true)}
                                                        className="text-yellow-500 hover:text-yellow-600 transition"
                                                        title="Remove from trending"
                                                    >
                                                        <FaStar className="inline text-yellow-500" /> Yes
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={() => handleToggleTrending(poster._id, false)}
                                                        className="text-gray-400 hover:text-yellow-500 transition"
                                                        title="Add to trending"
                                                    >
                                                        <FaStar className="inline" /> No
                                                    </button>
                                                )}
                                            </td>
                                            <td className="p-2 border">
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => handleView(poster)}
                                                        className="bg-green-500 hover:bg-green-600 text-white px-2 py-1 rounded transition"
                                                        title="View Details"
                                                    >
                                                        <FaEye />
                                                    </button>
                                                    <button
                                                        onClick={() => handleEdit(poster)}
                                                        className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded transition"
                                                        title="Edit"
                                                    >
                                                        <FaEdit />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(poster._id)}
                                                        className="bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded transition"
                                                        title="Delete"
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {posters.length > 0 && (
                        <div className="flex justify-between items-center mt-4 flex-wrap gap-2">
                            <div className="text-sm text-gray-600">
                                Showing {(currentPage - 1) * 10 + 1} to {Math.min(currentPage * 10, totalCount)} of {totalCount} entries
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded disabled:opacity-50 transition flex items-center gap-1"
                                    disabled={currentPage === 1}
                                >
                                    <FaArrowLeft size={12} /> Previous
                                </button>
                                <span className="px-4 py-2 bg-gray-100 rounded">
                                    Page {currentPage} of {totalPages}
                                </span>
                                <button
                                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded disabled:opacity-50 transition flex items-center gap-1"
                                    disabled={currentPage === totalPages}
                                >
                                    Next <FaArrowRight size={12} />
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Edit Modal */}
            {modalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
                        <div className="sticky top-0 bg-white p-4 border-b flex justify-between items-center">
                            <h3 className="text-lg font-semibold text-blue-900">Edit Poster</h3>
                            <button
                                onClick={() => setModalOpen(false)}
                                className="text-gray-500 hover:text-gray-700 transition"
                            >
                                <FaTimes size={20} />
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* <input
                                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none"
                                    placeholder="Name *"
                                    value={editedData.name}
                                    onChange={(e) => setEditedData({ ...editedData, name: e.target.value })}
                                /> */}
                                <input
                                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none"
                                    placeholder="Category"
                                    value={editedData.categoryName}
                                    onChange={(e) => setEditedData({ ...editedData, categoryName: e.target.value })}
                                />
                                {/* <input
                                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none"
                                    placeholder="Title *"
                                    value={editedData.title}
                                    onChange={(e) => setEditedData({ ...editedData, title: e.target.value })}
                                />
                                <input
                                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none"
                                    placeholder="Poster Language"
                                    value={editedData.posterlang}
                                    onChange={(e) => setEditedData({ ...editedData, posterlang: e.target.value })}
                                />
                                <input
                                    type="date"
                                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none"
                                    placeholder="Festival Date"
                                    value={editedData.festivalDate}
                                    onChange={(e) => setEditedData({ ...editedData, festivalDate: e.target.value })}
                                />
                                <input
                                    type="email"
                                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none"
                                    placeholder="Email"
                                    value={editedData.email}
                                    onChange={(e) => setEditedData({ ...editedData, email: e.target.value })}
                                />
                                <input
                                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none"
                                    placeholder="Mobile"
                                    value={editedData.mobile}
                                    onChange={(e) => setEditedData({ ...editedData, mobile: e.target.value })}
                                />
                                <input
                                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none"
                                    placeholder="Tags (comma separated)"
                                    value={editedData.tags}
                                    onChange={(e) => setEditedData({ ...editedData, tags: e.target.value })}
                                /> */}
                            </div>
                            {/* <textarea
                                className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none"
                                rows="4"
                                placeholder="Description"
                                value={editedData.description}
                                onChange={(e) => setEditedData({ ...editedData, description: e.target.value })}
                            /> */}
                        </div>
                        <div className="sticky bottom-0 bg-gray-50 p-4 border-t flex justify-end gap-2">
                            <button
                                onClick={() => setModalOpen(false)}
                                className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded transition"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveChanges}
                                className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded transition"
                            >
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* View Modal */}
            {viewModalOpen && viewingPoster && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-xl">
                        <div className="sticky top-0 bg-white p-4 border-b flex justify-between items-center">
                            <h3 className="text-lg font-semibold text-blue-900">Poster Details</h3>
                            <button
                                onClick={() => setViewModalOpen(false)}
                                className="text-gray-500 hover:text-gray-700 transition"
                            >
                                <FaTimes size={20} />
                            </button>
                        </div>
                        <div className="p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    {viewingPoster.posterImage && (
                                        <img
                                            src={typeof viewingPoster.posterImage === 'string' ? viewingPoster.posterImage : viewingPoster.posterImage.url}
                                            alt={viewingPoster.title}
                                            className="w-full rounded-lg shadow-md"
                                        />
                                    )}
                                </div>
                                <div className="space-y-3">
                                    {/* <div>
                                        <label className="font-semibold text-gray-700">Title:</label>
                                        <p className="text-gray-900">{viewingPoster.title || "N/A"}</p>
                                    </div>
                                    <div>
                                        <label className="font-semibold text-gray-700">Name:</label>
                                        <p className="text-gray-900">{viewingPoster.name || "N/A"}</p>
                                    </div> */}
                                    <div>
                                        <label className="font-semibold text-gray-700">Category:</label>
                                        <p className="text-gray-900">{viewingPoster.categoryName || "N/A"}</p>
                                    </div>
                                    {/* <div>
                                        <label className="font-semibold text-gray-700">Language:</label>
                                        <p className="text-gray-900 uppercase">{viewingPoster.posterlang || "N/A"}</p>
                                    </div>
                                    <div>
                                        <label className="font-semibold text-gray-700">Trending:</label>
                                        <p className={viewingPoster.isTrending ? "text-yellow-600" : "text-gray-600"}>
                                            {viewingPoster.isTrending ? "Yes" : "No"}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="font-semibold text-gray-700">Festival Date:</label>
                                        <p className="text-gray-900">
                                            {viewingPoster.festivalDate ? new Date(viewingPoster.festivalDate).toLocaleDateString() : "N/A"}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="font-semibold text-gray-700">Email:</label>
                                        <p className="text-gray-900">{viewingPoster.email || "N/A"}</p>
                                    </div>
                                    <div>
                                        <label className="font-semibold text-gray-700">Mobile:</label>
                                        <p className="text-gray-900">{viewingPoster.mobile || "N/A"}</p>
                                    </div>
                                    <div>
                                        <label className="font-semibold text-gray-700">Tags:</label>
                                        <div className="flex flex-wrap gap-1 mt-1">
                                            {viewingPoster.tags?.map((tag, idx) => (
                                                <span key={idx} className="px-2 py-1 bg-gray-200 rounded-full text-xs">
                                                    {tag}
                                                </span>
                                            )) || "N/A"}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="font-semibold text-gray-700">Description:</label>
                                        <p className="text-gray-900">{viewingPoster.description || "N/A"}</p>
                                    </div> */}
                                    <div>
                                        <label className="font-semibold text-gray-700">Created At:</label>
                                        <p className="text-gray-900">
                                            {viewingPoster.createdAt ? new Date(viewingPoster.createdAt).toLocaleString() : "N/A"}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Image Preview Modal */}
            {previewModalImage && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
                    <div className="relative">
                        <button
                            className="absolute -top-10 right-0 bg-red-500 hover:bg-red-600 text-white rounded-full p-2 transition"
                            onClick={() => setPreviewModalImage(null)}
                        >
                            <FaTimes size={20} />
                        </button>
                        <img
                            src={previewModalImage}
                            alt="Poster Preview"
                            className="max-w-full max-h-[90vh] rounded shadow-lg"
                        />
                    </div>
                </div>
            )}
        </div>
    );
}